/** @jsxImportSource @emotion/react */
import { Box, Grid, Stack, Typography } from "@mui/material";
import MailSection from "./MailSection";
import { l_footer } from "../locales";
import { LocalizedLink } from "../../../LocalizedLink";

interface Contact {
  contact:
    | {
        title: string;
        elements: (
          | {
              name: string;
              locallink: string;
            }
          | {
              name: string;
              locallink?: undefined;
            }
        )[];
      }
    | {
        title: string;
        elements: (
          | {
              name: string;
              link?: undefined;
            }
          | {
              name: string;
              link: string;
            }
        )[];
      }
    | {
        title: any;
        elements?: undefined;
      };
}

const ContactListItem: React.FC<Contact> = ({ contact }) => {
  return (
    <Box
      sx={{
        marginBottom: {
          md:
            contact.title === l_footer("gridSection.header4") ? "24px" : "0px",
          xs:
            contact.title === l_footer("gridSection.header4") ? "20px" : "32px",
        },
      }}
    >
      <Typography
        sx={{
          fontSize: {
            md: "14px",
            xs: "14px",
          },
          marginBottom: "12px",
          textTransform: "uppercase",
          color: "rgba(255, 255, 255, 0.74)",
        }}
      >
        {contact.title}
      </Typography>
      {contact.elements &&
        contact.elements?.map((listItem, index) => {
          if ("locallink" in listItem) {
            return (
              <Box sx={{ width: "fit-content" }} key={listItem.name + index}>
                <LocalizedLink
                  to={listItem.locallink ?? ""}
                  style={{
                    textDecoration: "none",
                    color: "white",
                  }}
                >
                  <Typography
                    sx={{
                      // width: "fit-content",
                      fontSize: "16px",
                      lineHeight: "20px",
                      marginBottom: "8px",
                      color: "rgba(255, 255, 255)",
                      ":hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {listItem.name}
                  </Typography>
                </LocalizedLink>
              </Box>
            );
          } else if ("link" in listItem) {
            return (
              <a
                key={listItem.name + index}
                href={listItem.link}
                target="_blank"
                css={{ textDecoration: "none", color: "white" }}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    lineHeight: "20px",
                    marginBottom: "8px",
                    color: "rgba(255, 255, 255)",
                    ":hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  {listItem.name}
                </Typography>
              </a>
            );
          } else {
            return (
              <Typography
                key={listItem.name + index}
                sx={{
                  fontSize: "16px",
                  lineHeight: "20px",
                  marginBottom: "8px",
                  color: "rgba(255, 255, 255)",
                  whiteSpace: "nowrap",
                }}
              >
                {listItem.name !== l_footer("gridSection.header5List1") ? (
                  listItem.name
                ) : (
                  <span>
                    Via Cadorna 2,
                    <br />
                    Albignasego Padova,
                    <br />
                    35020 Italia
                  </span>
                )}
              </Typography>
            );
          }
        })}
    </Box>
  );
};

const ContactsSection = ({
  withoutTopSection,
}: {
  withoutTopSection?: boolean;
}) => {
  const contacts = [
    {
      title: l_footer("gridSection.header1"),
      elements: [
        {
          name: l_footer("gridSection.header1List1"),
          locallink: "/",
        },
        {
          name: l_footer("gridSection.header1List2"),
          locallink: "/manifesto",
        },
        {
          name: l_footer("gridSection.header1List3"),
          locallink: "/works",
        },
        {
          name: l_footer("gridSection.header1List4"),
          locallink: "/life-at-mabiloft",
        },
        {
          name: l_footer("gridSection.header1List5"),
          locallink: "/blog",
        },
        {
          name: l_footer("gridSection.header1List6"),
          locallink: "/careers",
        },
        {
          name: l_footer("gridSection.header1List7"),
          locallink: "/contacts",
        },
      ],
    },
    {
      title: l_footer("gridSection.header2"),
      elements: [
        // {
        //   name: l_footer("gridSection.header2List1"),
        //   // link: "",
        // },
        {
          name: l_footer("gridSection.header2List2"),
          link: "https://javaspritz.dev/",
        },
        {
          name: l_footer("gridSection.header2List3"),
          link: "https://uxtea.it/",
        },
      ],
    },
    {
      title: l_footer("gridSection.header3"),
      elements: [
        {
          name: l_footer("gridSection.header3List1"),
          link: "https://www.instagram.com/mabiloft/?hl=en",
        },
        {
          name: l_footer("gridSection.header3List2"),
          link: "https://it.linkedin.com/company/mabiloft",
        },
        {
          name: l_footer("gridSection.header3List3"),
          link: "https://www.facebook.com/mabiloft/",
        },
        {
          name: l_footer("gridSection.header3List4"),
          link: "https://medium.com/@mabiloft",
        },
      ],
    },

    {
      title: l_footer("gridSection.header4"),
      elements: [
        {
          name: l_footer("gridSection.header4List1"),
          link: "mailto:hello@mabiloft.com",
        },
        {
          name: l_footer("gridSection.header4List2"),
          link: "tel:+390494732842",
        },
      ],
    },
    {
      title: l_footer("gridSection.header5"),
      elements: [{ name: l_footer("gridSection.header5List1") }],
    },
  ];

  return (
    <Stack
      sx={{
        flexDirection: { xs: "column", lg: "row" },
        gap: "60px",
        justifyContent: "space-between",

        borderWidth: "1px",
        borderStyle: "solid",
        borderTopColor: withoutTopSection
          ? "transparent"
          : "rgba(255, 255, 255, 0.12)",
        borderBottomColor: "rgba(255, 255, 255, 0.12)",

        paddingTop: "44px",
        paddingBottom: "44px",
      }}
    >
      <Box sx={{ maxWidth: { xs: "unset", md: "60%", lg: "50%" } }}>
        <MailSection />
      </Box>

      <Grid container>
        {contacts.map(
          (contact, i) =>
            i !== 3 &&
            i !== 4 && (
              <Grid
                key={contact.title + i}
                item
                xs={i % 3 === 0 ? 8 : 4}
                md={3}
                sx={{
                  order: { md: i, xs: i === 2 ? 3 : i },
                  // border: "1px solid pink",
                }}
              >
                <ContactListItem contact={contact} />
              </Grid>
            )
        )}
        <Grid item xs={8} md={3} sx={{ order: { md: 3, xs: 2 } }}>
          {contacts.map(
            (contact, i) =>
              (i === 3 || i === 4) && (
                <ContactListItem key={contact.title + i} contact={contact} />
              )
          )}
        </Grid>
      </Grid>
    </Stack>
  );
};
export default ContactsSection;
